// View — one side's main panel + side panel. Spec §6.3, §6.4, §7.3.
//
// PR8 fills the main panel with VideoCanvas inside a ZoomPanWrapper.
// PR9 adds the SVG overlay as a sibling inside ZoomPanWrapper; PR10 wires
// interactions. The active-view border wiring lives here so PR10 only needs
// to set `selection.activeView`, never edit this file.

import * as React from 'react';
import { COLORS } from '../../../config/constants';
import { t } from '../../../config/strings';
import { ImageCanvas } from '../../../ImageCanvas';
import { cn } from '../../../lib/cn';
import { useStore } from '../../../store';
import type { Side } from '../../../store/predictions';
import { OverlaySvg } from './OverlaySvg';
import { ResizeHandle } from './ResizeHandle';
import { SidePanel } from './SidePanel';
import { ZoomPanWrapper } from './ZoomPanWrapper';

export interface ViewProps {
  side: Side;
  readOnly?: boolean;
  /** Skip side panel + resize handle. Used in 4-view layout where panels are hoisted. */
  noPanel?: boolean;
}

export function View({ side, readOnly = false, noPanel = false }: ViewProps): React.ReactElement {
  const isActive = useStore((s) => s.activeView === side);
  const folder = useStore((s) => s.folder);
  const folderMeta = useStore((s) => s.folderMeta);
  // Per-side native size populated by VideoCanvas/ImageCanvas on load; falls
  // back to the prediction-JSON meta until the element has reported in. This
  // lets each view's aspect ratio self-adjust when cameras differ in size.
  const sideImageSize = useStore((s) => s.imageSizes[side]);
  const sidePanelWidth = useStore((s) => s.sidePanelWidth[side]);
  const sidePanelCollapsed = useStore((s) => s.sidePanelCollapsed[side]);
  const label = t.views[side];
  // Suspend ZoomPanWrapper panning for the duration of a keypoint drag so the
  // image doesn't slide out from under the cursor. OverlaySvg flips this on
  // pointer-down (unqualified, unlocked dot) and off on pointer-up/cancel.
  const [isDragging, setIsDragging] = React.useState(false);
  // Side panels live on the outer edges (front_left/left → left edge,
  // front_right/right → right edge) so the main panels meet in the middle.
  const sidePanelOnLeft = side === 'front_left' || side === 'left';

  const effectiveWidth = sidePanelCollapsed ? 0 : sidePanelWidth;

  const sidePanel = (
    <div
      className={cn(
        'shrink-0 overflow-hidden bg-background transition-[width] duration-150',
        sidePanelOnLeft ? 'border-r border-border' : 'border-l border-border'
      )}
      style={{ width: effectiveWidth }}
      data-testid={`side-panel-wrapper-${side}`}
    >
      <SidePanel side={side} />
    </div>
  );

  const handle = <ResizeHandle side={side} sidePanelOnLeft={sidePanelOnLeft} />;

  // Always-visible label bar above the video region identifies the view
  // (HEAD FRONT LEFT etc.). When the folder isn't loaded yet (e.g. tests that
  // mount AnnotateView in isolation) we still render the bar plus a centered
  // placeholder so existing assertions pass.
  const mainPanel = (
    <div className="flex min-w-0 flex-1 flex-col" data-testid={`main-panel-${side}`}>
      <div
        className="flex h-8 shrink-0 items-center border-b border-border bg-muted/40 px-3 text-sm font-medium text-muted-foreground"
        data-testid={`view-label-${side}`}
      >
        {label}
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center bg-muted text-2xl font-semibold uppercase text-muted-foreground">
        {folder && folderMeta ? (
          <ZoomPanWrapper
            imageSize={sideImageSize ?? folderMeta.imageSize}
            panningDisabled={isDragging}
          >
            <ImageCanvas side={side} />
            {readOnly ? (
              <OverlaySvg side={side} readOnly />
            ) : (
              <OverlaySvg side={side} onDragStateChange={setIsDragging} />
            )}
          </ZoomPanWrapper>
        ) : (
          label
        )}
      </div>
    </div>
  );

  return (
    <div
      className={cn('flex h-full overflow-hidden rounded-md border-2 border-transparent')}
      style={isActive ? { borderColor: COLORS.activeViewBorder } : undefined}
      data-testid={`view-${side}`}
    >
      {readOnly || noPanel ? (
        mainPanel
      ) : sidePanelOnLeft ? (
        <>
          {sidePanel}
          {handle}
          {mainPanel}
        </>
      ) : (
        <>
          {mainPanel}
          {handle}
          {sidePanel}
        </>
      )}
    </div>
  );
}
